'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import type { HousingProperty } from '@eliadmin/core/src/api/housing/housing-types';
import {
  checkHousingAvailability,
  createHousingReservation,
  fetchAvailableHousingProperties,
} from '@eliadmin/core/src/api/housing/housing-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { ReservationFormHeader } from '@eliadmin/core/src/components/reservation/reservation-form/reservation-form-header';
import { ReservationFormContent } from '@eliadmin/core/src/components/reservation/reservation-form/reservation-form-content';
import { ReservationFormFooter } from '@eliadmin/core/src/components/reservation/reservation-form/reservation-form-footer';
import { ReservationFormSuccess } from '@eliadmin/core/src/components/reservation/reservation-form/reservation-form-success';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import {
  reservationSchema,
  type ReservationFormInputValues,
  type ReservationFormValues,
} from '@eliadmin/core/src/components/reservation/reservation-form/reservation-form-schema';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  computeReservationPricing,
  countNights,
  formatSkDateRange,
  pluralGuests,
} from './date-utils';

const STEP_FOR_FIELD: Record<string, number> = {
  arrival: 1,
  departure: 1,
  guests_count: 1,
  property_id: 2,
  name: 3,
  surname: 3,
  email: 3,
  phone: 3,
  note: 3,
};

const BACKEND_TO_FORM_FIELD: Record<string, keyof ReservationFormValues> = {
  name: 'name',
  surname: 'surname',
  email: 'email',
  phone: 'phone',
  note: 'note',
  date_from: 'arrival',
  date_to: 'departure',
  person_count: 'guests_count',
};

const TOTAL_STEPS = 4;

const STEP_CONFIG: Record<number, { title: string; description: string }> = {
  1: {
    title: 'Overiť dostupnosť',
    description: 'Vyberte si dátum pobytu a počet hostí. Bez záväzku.',
  },
  2: {
    title: 'Dostupné ubytovanie',
    description: 'Vyberte si typ ubytovania, ktorý vám vyhovuje.',
  },
  3: {
    title: 'Vaše údaje',
    description:
      'Potrebujeme len základné info pre dokončenie rezervácie. Aktuálne nevkladáte žiadnu Platobnú kartu ani neplatíte zálohu.',
  },
  4: {
    title: 'Súhrn rezervácie',
    description:
      'Skontrolujte všetky údaje, ak ste našli chybu, vráťte sa na krok a údaje opravte.',
  },
};

const STEP_FIELDS: Record<number, string[]> = {
  1: ['arrival', 'departure', 'guests_count'],
  2: ['property_id'],
  3: ['name', 'surname', 'email'],
  4: ['gdpr', 'tos'],
};

function parseDateParam(value: string | undefined): Date | '' {
  if (!value) return '';
  try {
    const date = parseISO(value);
    return isNaN(date.getTime()) ? '' : date;
  } catch {
    return '';
  }
}

interface ReservationFormProps {
  properties: HousingProperty[];
  searchParams?: Record<string, string>;
}

export function ReservationForm({ properties, searchParams }: ReservationFormProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const queryClient = useQueryClient();

  const initialArrival = parseDateParam(searchParams?.arrival);
  const initialDeparture = parseDateParam(searchParams?.departure);
  const initialGuests = searchParams?.guests ? Math.max(2, parseInt(searchParams.guests) || 2) : 2;
  const initialPropertyId = searchParams?.property
    ? parseInt(searchParams.property) || properties[0]?.id || 1
    : properties[0]?.id || 1;

  const hasPrefilledBooking = !!(initialArrival && initialDeparture && searchParams?.property);

  const [currentStep, setCurrentStep] = useState(hasPrefilledBooking ? 2 : 1);

  const [submittedReservation, setSubmittedReservation] = useState<{
    submittedAt: Date;
    email: string;
    depositAmount: number;
  } | null>(null);

  const { formValidationMessages } = useFormValidationMessages(t);
  const schema = reservationSchema(formValidationMessages);

  const form = useForm<ReservationFormInputValues, any, ReservationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      arrival: initialArrival,
      departure: initialDeparture,
      guests_count: initialGuests,
      property_id: initialPropertyId,
      name: '',
      surname: '',
      email: '',
      phone: '',
      note: '',
      gdpr: false,
      tos: false,
      'g-recaptcha-response': '',
    },
  });

  const handleNext = async () => {
    const fields = STEP_FIELDS[currentStep] ?? [];
    const valid = await form.trigger(fields as any);
    if (!valid) return;

    const nextStep = Math.min(currentStep + 1, TOTAL_STEPS);
    // Step 4's checkboxes must only ever show as invalid after an actual
    // submit attempt on this visit — never as a leftover from an earlier
    // failed submit while the user was navigating back and forth.
    if (nextStep === TOTAL_STEPS) form.clearErrors(['gdpr', 'tos']);
    setCurrentStep(nextStep);
  };

  const handleBack = () => setCurrentStep(s => Math.max(s - 1, 1));

  const watchedArrival = useWatch({ control: form.control, name: 'arrival' });
  const watchedDeparture = useWatch({ control: form.control, name: 'departure' });
  const watchedGuests = Number(useWatch({ control: form.control, name: 'guests_count' })) || 2;

  const arrivalDate = watchedArrival instanceof Date ? watchedArrival : null;
  const departureDate = watchedDeparture instanceof Date ? watchedDeparture : null;
  const dateFrom = arrivalDate ? format(arrivalDate, 'yyyy-MM-dd') : undefined;
  const dateTo = departureDate ? format(departureDate, 'yyyy-MM-dd') : undefined;

  // Step 2 must only ever offer properties the backend just confirmed as
  // free for the chosen dates — never the unfiltered static property list.
  const availablePropertiesQuery = useQuery({
    queryKey: ['housing-properties-available', dateFrom, dateTo],
    queryFn: () =>
      fetchAvailableHousingProperties({
        locale,
        body: { date_from: dateFrom!, date_to: dateTo! },
      }).then(res => res.data),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 0,
  });

  // The backend only checks date availability — it doesn't know how many
  // guests were requested, so a property too small for the party still
  // comes back as "available". Filter those out here.
  const availableProperties = (availablePropertiesQuery.data ?? []).filter(
    property => property.capacity_total >= watchedGuests,
  );

  const handleSubmit = form.handleSubmit(async data => {
    const selectedProperty = availableProperties.find(p => p.id === data.property_id);

    if (!selectedProperty) {
      toast({
        title: 'Zvolené ubytovanie už nie je dostupné',
        description: 'Vyberte prosím iné ubytovanie pre zvolený termín.',
        variant: 'destructive',
      });
      setCurrentStep(2);
      return;
    }

    const dateFromStr = format(data.arrival, 'yyyy-MM-dd');
    const dateToStr = format(data.departure, 'yyyy-MM-dd');

    try {
      // Re-verify availability right before writing the reservation so a
      // booking made by someone else in the meantime can't slip through.
      const availability = await checkHousingAvailability({
        locale,
        body: {
          property: { id: selectedProperty.id },
          date_from: dateFromStr,
          date_to: dateToStr,
        },
      });

      if (!availability.data.property.available) {
        toast({
          title: 'Termín je už obsadený',
          description:
            'Niekto iný medzičasom zarezervoval tieto dni. Vyberte prosím iný termín alebo ubytovanie.',
          variant: 'destructive',
        });
        await queryClient.invalidateQueries({
          queryKey: ['housing-properties-available', dateFromStr, dateToStr],
        });
        setCurrentStep(2);
        return;
      }

      await createHousingReservation({
        locale,
        body: {
          property: { id: selectedProperty.id },
          zoom: null,
          name: data.name,
          surname: data.surname,
          email: data.email,
          phone: data.phone ?? '',
          date_from: dateFromStr,
          date_to: dateToStr,
          person_count: data.guests_count,
          note: data.note || undefined,
        },
      });

      const { deposit } = computeReservationPricing({
        nights: countNights(data.arrival, data.departure),
        guestsCount: data.guests_count,
        pricePerNight: Number(selectedProperty.price_per_night) || 0,
      });

      toast({ title: 'Rezervácia bola úspešne odoslaná', variant: 'success' });
      setSubmittedReservation({
        submittedAt: new Date(),
        email: data.email,
        depositAmount: deposit,
      });
    } catch (err) {
      if (err instanceof ValidationError) {
        let firstErroredStep: number | undefined;

        for (const [backendField, formField] of Object.entries(BACKEND_TO_FORM_FIELD)) {
          const messages = err.fields[backendField];
          if (!messages?.length) continue;

          form.setError(formField as any, { type: 'manual', message: messages.join('\n') });
          const step = STEP_FOR_FIELD[formField];
          if (step && (!firstErroredStep || step < firstErroredStep)) firstErroredStep = step;
        }

        if (firstErroredStep) setCurrentStep(firstErroredStep);

        toast({
          title: 'Skontrolujte prosím zadané údaje',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Rezerváciu sa nepodarilo odoslať',
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  const step2Description = (() => {
    if (arrivalDate && departureDate) {
      return `V termíne ${formatSkDateRange(arrivalDate, departureDate)} pre ${pluralGuests(watchedGuests)} máme k dispozícii:`;
    }
    return STEP_CONFIG[2].description;
  })();

  const stepDescription =
    currentStep === 2 ? step2Description : STEP_CONFIG[currentStep].description;

  if (submittedReservation) {
    return (
      <div
        className={cn(
          'relative z-10 flex w-full max-w-[792px] flex-col items-center justify-center gap-6 rounded-[24px] bg-white p-4 sm:gap-8 sm:p-6 md:p-8 lg:gap-12 lg:p-12',
          'ea-reservation-form',
        )}
      >
        <ReservationFormSuccess
          submittedAt={submittedReservation.submittedAt}
          email={submittedReservation.email}
          depositAmount={submittedReservation.depositAmount}
        />
      </div>
    );
  }

  return (
    <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
      <form
        onSubmit={handleSubmit}
        className={cn(
          'relative z-10 flex w-full max-w-[792px] flex-col items-center justify-center gap-6 rounded-[24px] bg-white p-4 sm:gap-8 sm:p-6 md:p-8 lg:gap-12 lg:p-12',
          'ea-reservation-form',
        )}
      >
        <ReservationFormHeader
          description={stepDescription}
          title={STEP_CONFIG[currentStep].title}
          step={currentStep}
          totalSteps={TOTAL_STEPS}
        />
        <ReservationFormContent
          step={currentStep}
          properties={availableProperties}
          isLoadingProperties={availablePropertiesQuery.isLoading}
          isErrorProperties={availablePropertiesQuery.isError}
          onGoToStep={setCurrentStep}
        />
        <ReservationFormFooter
          step={currentStep}
          totalSteps={TOTAL_STEPS}
          isSubmitting={form.formState.isSubmitting}
          onNext={handleNext}
          onBack={handleBack}
        />
        <div className="flex w-full items-center justify-center gap-[10px]">
          <ButtonIcon url="/images/icons/security-safe.svg" alt={t('text3767')} />
          <p className="text-center text-xs text-secondary sm:text-sm">{t('text3767')}</p>
        </div>
      </form>
    </Form>
  );
}
